import React, { useCallback, useEffect, useRef, useState } from 'react';
import { __ } from '@wordpress/i18n';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';

// store
import { storeSet, useStoreValue } from '../../../../store';

// components
import BadgeTrack from '../../../../components/BadgeTrack';
import ButtonIconSimple from '../../../../components/ButtonWrapper/components/ButtonIconSimple';
import DownloadIcon from '../../../../icons/DownloadIcon';
import ClipService from '../../../../service/clip-service';
import displayNetworkError from '../../../../helpers/displayNetworkError';

// ─── Constants ───────────────────────────────────────────────────────────────

const DAY_WIDTH    = 40;  // px — also set as CSS var on root canvas
const ROW_HEIGHT   = 42;  // px — must match CSS
const SIDEBAR_WIDTH = 170; // px — must match CSS

const CUE_TYPE_COLOR = {
    MUSIC:     { bg: 'rgba(77,201,252,0.12)',  border: '#4DC9FC', text: '#4DC9FC' },
    SFX:       { bg: 'rgba(109,204,109,0.12)', border: '#6DCC6D', text: '#6DCC6D' },
    MIX:       { bg: 'rgba(255,165,80,0.12)',  border: '#FFA550', text: '#FFA550' },
    REFERENCE: { bg: 'rgba(170,120,255,0.12)', border: '#AA78FF', text: '#AA78FF' },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const addDays = (date, n) => {
    const d = new Date(date);
    d.setDate(d.getDate() + n);
    return d;
};

const diffDays = (a, b) => Math.round((b - a) / 86400000);

const stripTime = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const MONTH_NAMES = [
    'January','February','March','April','May','June',
    'July','August','September','October','November','December',
];

const withMockDates = (items) => {
    const today = stripTime(new Date());
    return items.map((item, i) => {
        const startOffset  = (i * 4) - 8;
        const durationDays = Math.max(3, Math.min(12, 3 + (i % 5) * 2));
        return {
            ...item,
            ganttStart: addDays(today, startOffset),
            ganttEnd:   addDays(today, startOffset + durationDays),
        };
    });
};

const buildDayColumns = (rangeStart, rangeEnd) => {
    const days = [];
    const cur  = new Date(rangeStart);
    while (cur <= rangeEnd) {
        days.push({ date: new Date(cur), dayNum: cur.getDate(), monthIndex: cur.getMonth(), year: cur.getFullYear() });
        cur.setDate(cur.getDate() + 1);
    }
    return days;
};

const buildMonthGroups = (days) => {
    const groups = [];
    for (const day of days) {
        const last = groups[groups.length - 1];
        if (last && last.monthIndex === day.monthIndex && last.year === day.year) {
            last.count++;
        } else {
            groups.push({ monthIndex: day.monthIndex, year: day.year, count: 1 });
        }
    }
    return groups;
};

// ─── Component ───────────────────────────────────────────────────────────────

const ProjectCalendar = ({ projectId }) => {
    const cueSheet      = useStoreValue('cueSheet');
    const lastProjectId = useRef(null);
    const resizeRef     = useRef(null);
    const dragRef       = useRef(null);

    const [itemDates, setItemDates] = useState({});

    const today      = stripTime(new Date());
    const rangeStart = stripTime(addDays(today, -30));
    const rangeEnd   = stripTime(addDays(today, 60));
    const days        = buildDayColumns(rangeStart, rangeEnd);
    const monthGroups = buildMonthGroups(days);
    const totalDays   = days.length;
    const todayCol    = diffDays(rangeStart, today);

    // ── Data loading ─────────────────────────────────────────────────────────

    useEffect(() => {
        storeSet('fetchCueSheet', projectId);
        if (lastProjectId.current !== projectId) {
            lastProjectId.current = projectId;
            storeSet('refetchCueSheet', projectId);
        }
    }, [projectId]);

    useEffect(() => {
        if (!cueSheet.length) return;
        const map = {};
        for (const item of withMockDates(cueSheet)) {
            map[item.id] = { start: item.ganttStart, end: item.ganttEnd };
        }
        setItemDates(map);
    }, [cueSheet]);

    // ── Resize interaction ───────────────────────────────────────────────────

    useEffect(() => {
        const onMouseMove = (e) => {
            if (resizeRef.current) {
                const { id, edge, startX, originalStart, originalEnd } = resizeRef.current;
                const deltaDays = Math.round((e.clientX - startX) / DAY_WIDTH);
                if (deltaDays === 0) return;
                setItemDates(prev => {
                    const cur = prev[id];
                    if (!cur) return prev;
                    let s = new Date(cur.start);
                    let e2 = new Date(cur.end);
                    if (edge === 'left') {
                        s = addDays(originalStart, deltaDays);
                        if (diffDays(s, e2) < 1) s = addDays(e2, -1);
                    } else {
                        e2 = addDays(originalEnd, deltaDays);
                        if (diffDays(s, e2) < 1) e2 = addDays(s, 1);
                    }
                    return { ...prev, [id]: { start: s, end: e2 } };
                });
                return;
            }
            if (dragRef.current) {
                const { id, startX, originalStart, originalEnd } = dragRef.current;
                const deltaDays = Math.round((e.clientX - startX) / DAY_WIDTH);
                if (deltaDays === 0) return;
                setItemDates(prev => {
                    if (!prev[id]) return prev;
                    return {
                        ...prev,
                        [id]: {
                            start: addDays(originalStart, deltaDays),
                            end:   addDays(originalEnd,   deltaDays),
                        },
                    };
                });
            }
        };
        const onMouseUp = () => {
            resizeRef.current = null;
            dragRef.current   = null;
            document.body.style.cursor    = '';
            document.body.style.userSelect = '';
        };
        document.addEventListener('mousemove', onMouseMove);
        document.addEventListener('mouseup',   onMouseUp);
        return () => {
            document.removeEventListener('mousemove', onMouseMove);
            document.removeEventListener('mouseup',   onMouseUp);
        };
    }, []);

    const handleResizeStart = useCallback((e, id, edge) => {
        e.preventDefault();
        e.stopPropagation();
        const cur = itemDates[id];
        if (!cur) return;
        resizeRef.current = { id, edge, startX: e.clientX, originalStart: new Date(cur.start), originalEnd: new Date(cur.end) };
        document.body.style.cursor    = 'ew-resize';
        document.body.style.userSelect = 'none';
    }, [itemDates]);

    const handleDragStart = useCallback((e, id) => {
        e.preventDefault();
        const cur = itemDates[id];
        if (!cur) return;
        dragRef.current = {
            id,
            startX:        e.clientX,
            originalStart: new Date(cur.start),
            originalEnd:   new Date(cur.end),
        };
        document.body.style.cursor    = 'grabbing';
        document.body.style.userSelect = 'none';
    }, [itemDates]);

    // ── Download ─────────────────────────────────────────────────────────────

    const handleDownload = async (clip) => {
        try {
            const data = await ClipService.downloadClipAsync(clip.id);
            const file = new Blob([data], { type: clip.mimeType });
            const url  = window.URL.createObjectURL(file);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', clip.name);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            displayNetworkError(error);
        }
    };

    // ── Render ───────────────────────────────────────────────────────────────

    const canvasWidth = SIDEBAR_WIDTH + totalDays * DAY_WIDTH;

    return (
        <div className="projectCalendar" role="region" aria-label={__('Calendar view', 'soundbetween')}>
            <OverlayScrollbarsComponent
                element="div"
                className="projectCalendar__scroller"
                options={{ scrollbars: { autoHide: 'move' } }}
                defer>

                {/* One wide canvas — header sticks to top, sidebar cells stick to left */}
                <div className="projectCalendar__canvas" style={{ minWidth: canvasWidth }}>

                    {/* ── Sticky header ── */}
                    <div className="projectCalendar__header">
                        {/* Sticky-left blank cell matching sidebar width */}
                        <div className="projectCalendar__header-sidebar" />

                        {/* Month + day columns */}
                        <div className="projectCalendar__header-timeline" style={{ width: totalDays * DAY_WIDTH }}>
                            <div className="projectCalendar__months-row">
                                {monthGroups.map((g, i) => (
                                    <div
                                        key={i}
                                        className={`projectCalendar__month-cell${g.monthIndex === today.getMonth() && g.year === today.getFullYear() ? ' projectCalendar__month-cell--current' : ''}`}
                                        style={{ width: g.count * DAY_WIDTH }}>
                                        {MONTH_NAMES[g.monthIndex]}
                                    </div>
                                ))}
                            </div>
                            <div className="projectCalendar__days-row">
                                {days.map((d, i) => (
                                    <div
                                        key={i}
                                        className={`projectCalendar__day-cell${i === todayCol ? ' projectCalendar__day-cell--today' : ''}`}
                                        style={{ width: DAY_WIDTH }}>
                                        {d.dayNum}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Item rows ── */}
                    {cueSheet.map((item) => {
                        const dates = itemDates[item.id];
                        const color = CUE_TYPE_COLOR[item.cueType] ?? CUE_TYPE_COLOR.MUSIC;

                        let barLeft = 0, barWidth = 0, isVisible = false;
                        if (dates) {
                            const startDay = diffDays(rangeStart, stripTime(dates.start));
                            const endDay   = diffDays(rangeStart, stripTime(dates.end));
                            barLeft  = startDay * DAY_WIDTH;
                            barWidth = Math.max(DAY_WIDTH, (endDay - startDay) * DAY_WIDTH);
                            isVisible = endDay > 0 && startDay < totalDays;
                        }

                        return (
                            <div className="projectCalendar__row" key={item.id} style={{ height: ROW_HEIGHT }}>

                                {/* Sticky-left sidebar cell */}
                                <div className="projectCalendar__sidebar-cell">
                                    <div
                                        className="projectCalendar__sidebar-item"
                                        style={{ '--item-color': color.border }}>
                                        <BadgeTrack>{item.label}</BadgeTrack>
                                        {item.clip
                                            ? <ButtonIconSimple
                                                onClick={() => handleDownload(item.clip)}
                                                aria-label={`${__('Download', 'soundbetween')} ${item.title}`}>
                                                <DownloadIcon/>
                                            </ButtonIconSimple>
                                            : null}
                                    </div>
                                </div>

                                {/* Track area with CSS gradient grid lines */}
                                <div
                                    className="projectCalendar__track-cell"
                                    style={{
                                        width: totalDays * DAY_WIDTH,
                                        '--today-offset': `${todayCol * DAY_WIDTH}px`,
                                    }}>

                                    {isVisible
                                        ? <div
                                            className="projectCalendar__bar"
                                            role="button"
                                            tabIndex={0}
                                            aria-label={`${item.label} – ${item.title}`}
                                            style={{
                                                left:        barLeft,
                                                width:       barWidth,
                                                borderColor: color.border,
                                            }}>

                                            <div
                                                className="projectCalendar__bar-handle projectCalendar__bar-handle--left"
                                                onMouseDown={(e) => handleResizeStart(e, item.id, 'left')}
                                                aria-hidden="true"
                                            />

                                            <div
                                                className="projectCalendar__bar-content"
                                                style={{ '--item-color': color.border }}
                                                onMouseDown={(e) => handleDragStart(e, item.id)}>
                                                <BadgeTrack>{item.label}</BadgeTrack>
                                                {barWidth > 90
                                                    ? <span className="projectCalendar__bar-title" style={{ color: color.text }}>
                                                        {item.title}
                                                    </span>
                                                    : null}
                                            </div>

                                            <div
                                                className="projectCalendar__bar-handle projectCalendar__bar-handle--right"
                                                onMouseDown={(e) => handleResizeStart(e, item.id, 'right')}
                                                aria-hidden="true"
                                            />
                                        </div>
                                        : null}
                                </div>
                            </div>
                        );
                    })}

                </div>
            </OverlayScrollbarsComponent>
        </div>
    );
};

export default ProjectCalendar;
